import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Button,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Textarea,
  IconButton,
  HStack,
  Badge,
  Text,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { gql, useQuery, useMutation, useApolloClient, ApolloQueryResult } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { useAuth } from "../../contexts/AuthContext";
import { getColor } from "../../brandConfig";

const GET_ALL_PROJECTS = gql`
  query GetAllProjects {
    projects {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
      members {
        id
        fName
        lName
        email
      }
      tasks {
        id
        description
        status
        order
        billed
        assignedTo {
          id
          fName
          lName
        }
        media {
          url
          description
          fileType
        }
      }
      bills {
        id
        lineItems {
          id
          amount
        }
        isPaid
        status
      }
    }
  }
`;

const GET_PROJECT_WITH_TASKS = gql`
  query GetProjectWithTasks($projectId: ID!) {
    project(id: $projectId) {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
      tasks {
        id
        description
        status
        order
        billed
        assignedTo {
          id
          fName
          lName
        }
        media {
          url
          description
          fileType
        }
      }
    }
  }
`;

const CREATE_BILL = gql`
  mutation CreateBill($input: BillInput!) {
    createBill(input: $input) {
      id
      isPaid
      paymentMethod
      currency
      acceptCardPayment
      acceptCryptoPayment
      lineItems {
        id
        amount
        description
      }
      projectId
      createdAt
      updatedAt
    }
  }
`;

interface LineItem {
  description: string;
  amount: number | "";
  taskId?: string; // Optional taskId to track which task this line item is related to
  status?: string; // Optional task status
}

const NewBill = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApolloClient(); // Get Apollo client instance
  const { user } = useAuth(); // Get current authenticated user
  
  // Consistent styling from brandConfig
  const bg = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");

  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('projectId');
  
  const [selectedProject, setSelectedProject] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"AUD_TRANSFER" | "CRYPTO" | "STRIPE">("AUD_TRANSFER");
  const [currency, setCurrency] = useState<"AUD" | "USD">("AUD");
  const [acceptCardPayment, setAcceptCardPayment] = useState(true);
  const [acceptCryptoPayment, setAcceptCryptoPayment] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([{ description: "", amount: "" }]);

  // Calculate total amount
  const totalAmount = lineItems.reduce((sum, item) => {
    const amount = typeof item.amount === 'number' ? item.amount : parseFloat(item.amount as string) || 0;
    return sum + amount;
  }, 0);

  const { loading: projectsLoading, error: projectsError, data: projectsData } = useQuery(GET_ALL_PROJECTS, {
    onCompleted: (data) => {
      console.log("Project data received from query:", data);
      if (data?.projects) {
        console.log(`Received ${data.projects.length} projects`);
        // Check the structure of the first project if available
        if (data.projects.length > 0) {
          const sampleProject = data.projects[0];
          console.log("Sample project structure:", {
            id: sampleProject.id,
            name: sampleProject.projectName,
            tasksCount: sampleProject.tasks?.length || 0,
            hasTasks: Array.isArray(sampleProject.tasks),
            billsCount: sampleProject.bills?.length || 0,
            firstTaskBilled: sampleProject.tasks && sampleProject.tasks.length > 0
              ? sampleProject.tasks[0].billed
              : "No tasks"
          });
        }
      } else {
        console.log("No projects data received");
      }
    },
    onError: (error) => {
      console.error("Error fetching projects:", error);
    }
  });
  const [createBill, { loading: creatingBill }] = useMutation(CREATE_BILL);

  // Pre-populate line items when a project is selected
  const handleProjectSelect = (projectId: string) => {
    console.log("Project selected:", projectId);
    setSelectedProject(projectId);

    if (!projectId) {
      console.log("No project selected, resetting line items");
      setLineItems([{ description: "", amount: "" }]);
      return;
    }

    // Set a loading line item while we fetch data
    setLineItems([{ description: "Loading tasks...", amount: "" }]);

    // Fetch the specific project with its tasks
    client.query<{ project: any }>({
      query: GET_PROJECT_WITH_TASKS,
      variables: { projectId },
      fetchPolicy: "network-only" // Force a fresh fetch
    }).then((response: ApolloQueryResult<{ project: any }>) => {
      console.log("Project details fetched:", response.data);

      const projectData = response.data.project;
      if (!projectData) {
        console.log("Project data not found");
        setLineItems([{ description: "", amount: "" }]);
        toast({
          title: "Error",
          description: "Could not load project details",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      console.log("Project tasks:", projectData.tasks);
      console.log("Task count:", projectData.tasks?.length || 0);

      // Check if tasks exist
      if (!projectData.tasks || projectData.tasks.length === 0) {
        console.log("No tasks found in this project");
        setLineItems([{ description: "", amount: "" }]);
        toast({
          title: "No tasks",
          description: "This project has no tasks. You can add custom line items.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Get tasks that aren't already billed
      const unbilledTasks = projectData.tasks.filter((task: any) => {
        console.log(`Task ${task.id} - ${task.description} - Billed:`, task.billed);
        // We need to check explicitly for false since billed is a boolean
        return task.billed === false;
      });

      console.log("Unbilled tasks:", unbilledTasks);
      console.log("Unbilled task count:", unbilledTasks.length);

      if (unbilledTasks.length === 0) {
        // If no unbilled tasks, keep an empty line item
        console.log("No unbilled tasks found");
        setLineItems([{ description: "", amount: "" }]);
        toast({
          title: "No unbilled tasks",
          description: "This project has no unbilled tasks. You can add custom line items.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Create line items from unbilled tasks
      const newLineItems = unbilledTasks.map((task: any) => {
        console.log(`Creating line item for task: ${task.description}`);
        return {
          description: task.description,
          amount: "", // Default to empty, user will need to set amount
          taskId: task.id, // Store the task ID for reference
          status: task.status // Store the task status for reference
        };
      });

      console.log("New line items:", newLineItems);
      setLineItems(newLineItems);

      toast({
        title: "Tasks loaded",
        description: `Loaded ${newLineItems.length} unbilled tasks as line items.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }).catch(error => {
      console.error("Error fetching project details:", error);
      setLineItems([{ description: "", amount: "" }]);
      toast({
        title: "Error",
        description: "Failed to load project tasks. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    });
  };

  // Auto-select project from URL parameter when projects data is loaded
  useEffect(() => {
    if (projectIdFromUrl && projectsData?.projects && !selectedProject) {
      const projectExists = projectsData.projects.some((p: any) => p.id === projectIdFromUrl);
      if (projectExists) {
        console.log("Auto-selecting project from URL:", projectIdFromUrl);
        handleProjectSelect(projectIdFromUrl);
      }
    }
  }, [projectIdFromUrl, projectsData, selectedProject]);

  const handleAddLineItem = () => {
    setLineItems([...lineItems, { description: "", amount: "" }]);
  };

  const handleRemoveLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    const newLineItems = [...lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: field === "amount" ? (value === "" ? "" : parseFloat(value as string) || 0) : value,
    };
    setLineItems(newLineItems);
  };

  const handleSubmit = async () => {
    console.log("🚀 Starting bill creation...");
    
    if (!selectedProject) {
      console.error("❌ No project selected");
      toast({
        title: "Error",
        description: "Please select a project",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Filter out line items with no description or zero amount
    const validLineItems = lineItems.filter(item => item.description && item.amount && Number(item.amount) > 0);
    console.log(`✅ Valid line items: ${validLineItems.length}`);

    if (validLineItems.length === 0) {
      console.error("❌ No valid line items");
      toast({
        title: "Error",
        description: "Please add at least one line item with a description and amount",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Remove taskId and status from lineItems before submitting
      const cleanedLineItems = validLineItems.map(({ description, amount }) => ({
        description,
        amount: Number(amount)
      }));

      const billInput = {
        projectId: selectedProject,
        isPaid,
        paymentMethod,
        currency,
        acceptCardPayment,
        acceptCryptoPayment,
        lineItems: cleanedLineItems,
        issuedBy: user?.id, // Include the current user's ID as the issuer
      };

      console.log("📋 Creating bill with:", {
        ...billInput,
        totalAmount: totalAmount.toFixed(2),
        currency,
        lineItemsCount: cleanedLineItems.length
      });

      const { data } = await createBill({
        variables: {
          input: billInput,
        },
      });

      console.log("✅ Bill created successfully:", {
        billId: data.createBill.id,
        currency: data.createBill.currency,
        totalLineItems: data.createBill.lineItems?.length
      });

      toast({
        title: "Success",
        description: `Bill created successfully in ${currency}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate(`/bill/${data.createBill.id}`);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create bill",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Heading color={textPrimary}>📝 Create New Bill</Heading>

          <Box
            p={6}
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel color={textPrimary}>Project</FormLabel>
                <Select
                  placeholder="Select project"
                  value={selectedProject}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  isDisabled={projectsLoading}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor={cardBorder}
                  color={textPrimary}
                  _hover={{ borderColor: textSecondary }}
                >
                  {projectsData?.projects.map((project: any) => (
                    <option key={project.id} value={project.id} style={{ backgroundColor: '#1a1a1a' }}>
                  {project.projectName} - {project.billingClient?.businessName || `${project.billingClient?.fName} ${project.billingClient?.lName}`}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={textPrimary}>Payment Method</FormLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as any)}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor={cardBorder}
                  color={textPrimary}
                  _hover={{ borderColor: textSecondary }}
                >
                  <option value="AUD_TRANSFER" style={{ backgroundColor: '#1a1a1a' }}>AUD Transfer</option>
                  <option value="CRYPTO" style={{ backgroundColor: '#1a1a1a' }}>Crypto</option>
                  <option value="STRIPE" style={{ backgroundColor: '#1a1a1a' }}>Stripe</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={textPrimary}>Currency</FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => {
                    const newCurrency = e.target.value as "AUD" | "USD";
                    setCurrency(newCurrency);
                    console.log(`Currency changed to: ${newCurrency}`);
                  }}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor={cardBorder}
                  color={textPrimary}
                  _hover={{ borderColor: textSecondary }}
                >
                  <option value="AUD" style={{ backgroundColor: '#1a1a1a' }}>AUD - Australian Dollar</option>
                  <option value="USD" style={{ backgroundColor: '#1a1a1a' }}>USD - US Dollar</option>
                </Select>
              </FormControl>

              {/* Payment Method Toggles - Only visible for DRAFT bills */}
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={textPrimary} mb={3}>
                  Payment Methods Available to Customer
                </Text>
                <VStack align="stretch" spacing={3}>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="card-payment" mb="0" color={textPrimary}>
                      Accept Card Payments (Stripe)
                    </FormLabel>
                    <Switch
                      id="card-payment"
                      isChecked={acceptCardPayment}
                      onChange={(e) => setAcceptCardPayment(e.target.checked)}
                      colorScheme="blue"
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="crypto-payment" mb="0" color={textPrimary}>
                      Accept Crypto Payments (Bitcoin)
                    </FormLabel>
                    <Switch
                      id="crypto-payment"
                      isChecked={acceptCryptoPayment}
                      onChange={(e) => setAcceptCryptoPayment(e.target.checked)}
                      colorScheme="blue"
                    />
                  </FormControl>
                </VStack>
                <Text fontSize="xs" color={textMuted} mt={2}>
                  Note: These options can only be changed while the bill is in DRAFT status
                </Text>
              </Box>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" color={textPrimary}>Is Paid?</FormLabel>
                <Switch isChecked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} />
              </FormControl>
            </VStack>
          </Box>

          <Box
            p={6}
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <HStack mb={4} justify="space-between">
              <Heading size="md" color={textPrimary}>Line Items</Heading>
              {selectedProject && (
                <Text fontSize="sm" color={textMuted}>
                  Line items are pre-populated with unbilled tasks from the selected project
                </Text>
              )}
            </HStack>
            <Box overflowX="auto" overflowY="hidden">
              <Table variant="simple" size={{ base: "sm", md: "md" }}>
                <Thead>
                  <Tr>
                    <Th color={textSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }}>Description</Th>
                    <Th color={textSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }}>Amount</Th>
                    <Th color={textSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} display={{ base: "none", md: "table-cell" }}>Status</Th>
                    <Th color={textSecondary} fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }}>Action</Th>
                  </Tr>
                </Thead>
              <Tbody>
                {lineItems.map((item, index) => (
                  <Tr key={index}>
                    <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                      <Textarea
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                        placeholder="Item description"
                        bg="rgba(0, 0, 0, 0.2)"
                        borderColor={cardBorder}
                        color={textPrimary}
                        _placeholder={{ color: textMuted }}
                        _hover={{ borderColor: textSecondary }}
                        minH={{ base: "80px", md: "120px" }}
                        resize="vertical"
                        rows={5}
                        fontSize={{ base: "sm", md: "md" }}
                      />
                    </Td>
                    <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                      <Input
                        type="number"
                        value={item.amount}
                        onChange={(e) => handleLineItemChange(index, "amount", e.target.value)}
                        placeholder="Amount"
                        bg="rgba(0, 0, 0, 0.2)"
                        borderColor={cardBorder}
                        color={textPrimary}
                        _placeholder={{ color: textMuted }}
                        _hover={{ borderColor: textSecondary }}
                        fontSize={{ base: "sm", md: "md" }}
                        minW={{ base: "80px", md: "120px" }}
                      />
                    </Td>
                    <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }} display={{ base: "none", md: "table-cell" }}>
                      {item.status && (
                        <Badge 
                          bg={
                            item.status === "COMPLETED" ? "rgba(34, 197, 94, 0.2)" :
                            item.status === "IN_PROGRESS" ? "rgba(251, 191, 36, 0.2)" : "rgba(107, 114, 128, 0.2)"
                          }
                          color={
                            item.status === "COMPLETED" ? "#22C55E" :
                            item.status === "IN_PROGRESS" ? "#FBBF24" : "#6B7280"
                          }
                          border="1px solid"
                          borderColor={
                            item.status === "COMPLETED" ? "rgba(34, 197, 94, 0.3)" :
                            item.status === "IN_PROGRESS" ? "rgba(251, 191, 36, 0.3)" : "rgba(107, 114, 128, 0.3)"
                          }
                        >
                          {item.status}
                        </Badge>
                      )}
                    </Td>
                    <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                      <IconButton
                        aria-label="Remove line item"
                        icon={<DeleteIcon />}
                        onClick={() => handleRemoveLineItem(index)}
                        isDisabled={lineItems.length === 1}
                        size={{ base: "sm", md: "md" }}
                        variant="ghost"
                        colorScheme="red"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
              </Table>
            </Box>
            <Button
              leftIcon={<AddIcon />}
              onClick={handleAddLineItem}
              mt={4}
              size="sm"
              bg="white"
              color="black"
              _hover={{
                bg: "gray.100",
                transform: "translateY(-2px)"
              }}
            >
              Add Line Item
            </Button>
          </Box>

          <HStack spacing={4}>
            <Button
              onClick={handleSubmit}
              isLoading={creatingBill}
              bg="white"
              color="black"
              _hover={{
                bg: "gray.100",
                transform: "translateY(-2px)"
              }}
            >
              Create Bill
            </Button>
            <Button 
              onClick={() => navigate("/bills")}
              variant="outline"
              borderColor={cardBorder}
              color={textPrimary}
              _hover={{
                borderColor: textSecondary,
                bg: "rgba(255, 255, 255, 0.05)"
              }}
            >
              Cancel
            </Button>
          </HStack>
        </VStack>
      </Container>
      <FooterWithFourColumns />
      
      {/* Floating Total Display */}
      <Box
        position="fixed"
        bottom={8}
        right={8}
        p={6}
        bg="linear-gradient(135deg, rgba(20, 20, 20, 0.95) 0%, rgba(30, 30, 30, 0.9) 50%, rgba(20, 20, 20, 0.95) 100%)"
        backdropFilter="blur(20px)"
        borderRadius="2xl"
        boxShadow="0 10px 40px rgba(0, 0, 0, 0.5), 0 0 60px rgba(54, 158, 255, 0.15)"
        borderWidth="1px"
        borderColor="rgba(54, 158, 255, 0.3)"
        transition="all 0.3s ease"
        _hover={{
          transform: "translateY(-2px) scale(1.02)",
          boxShadow: "0 15px 50px rgba(0, 0, 0, 0.6), 0 0 80px rgba(54, 158, 255, 0.25)",
          borderColor: "rgba(54, 158, 255, 0.5)"
        }}
        zIndex={1000}
      >
        <VStack spacing={2} align="stretch">
          <Text
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="wider"
            color="rgba(255, 255, 255, 0.6)"
          >
            Total Amount
          </Text>
          <HStack spacing={1} align="baseline">
            <Text
              fontSize="3xl"
              fontWeight="bold"
              color="#FFFFFF"
              lineHeight="1"
            >
              ${totalAmount.toFixed(2)} {currency}
            </Text>
            <Text
              fontSize="sm"
              color="rgba(255, 255, 255, 0.5)"
              fontWeight="medium"
            >
              AUD
            </Text>
          </HStack>
          {lineItems.filter(item => item.amount && Number(item.amount) > 0).length > 0 && (
            <Text
              fontSize="xs"
              color="rgba(54, 158, 255, 0.8)"
            >
              {lineItems.filter(item => item.amount && Number(item.amount) > 0).length} item{lineItems.filter(item => item.amount && Number(item.amount) > 0).length !== 1 ? 's' : ''}
            </Text>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default NewBill; 